import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Building2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTranslation } from 'react-i18next';

interface BoardDevice {
  deviceId: string;
  modelo: string;
  nroSerie: string;
  status: string;
}

interface Props {
  device: BoardDevice;
  baja: {
    idBaja: string;
    correo: string;
    nombre?: string | null;
    personalEmail?: string | null;
    pais?: string | null;
  };
}

/**
 * Acciones por equipo de una baja en el board de OMs:
 *  - "Quedó en oficina": el equipo vuelve al stock (Lista para Entregar, sin owner).
 *  - "Retiro Quipteams": pide el retiro al domicilio del clapper (mail personal
 *    prellenado desde la planilla de altas, editable).
 */
export function OmBajaDeviceActions({ device, baja }: Props) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [pickupOpen, setPickupOpen] = useState(false);
  const [form, setForm] = useState({
    personalEmail: baja.personalEmail || '',
    country: baja.pais || 'Argentina',
    address: '',
    addressExtra: '',
    phone: '',
    whenToContact: '',
    notes: '',
  });

  const invalidate = () => utils.officeManager.getBoard.invalidate();

  const keepMutation = trpc.officeManager.keepDeviceInOffice.useMutation({
    onSuccess: () => {
      toast.success(`${device.modelo} (${device.nroSerie}) vuelve al stock de la oficina`);
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const pickupMutation = trpc.officeManager.requestPickup.useMutation({
    onSuccess: (r) => {
      toast.success(`Retiro pedido a Quipteams (acción ${r.actionId})`);
      setPickupOpen(false);
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const pending = keepMutation.isPending || pickupMutation.isPending;

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1"
        disabled={pending}
        onClick={() => {
          if (
            window.confirm(
              `¿El equipo ${device.modelo} (${device.nroSerie}) quedó en la oficina?\n\nVuelve al stock como "Lista para Entregar" y se registra el recupero.`
            )
          ) {
            keepMutation.mutate({
              deviceId: device.deviceId,
              bajaId: baja.idBaja,
              correoWinclap: baja.correo,
              employeeName: baja.nombre || undefined,
              country: baja.pais || undefined,
            });
          }
        }}
      >
        {keepMutation.isPending ? <WinclapLoader size={14} /> : <Building2 className="h-3.5 w-3.5" />}
        {t('office-manager.ombajadeviceactions.quedo_en_oficina')}
      </Button>

      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1 border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
        disabled={pending}
        onClick={() => setPickupOpen(true)}
      >
        <Truck className="h-3.5 w-3.5" />
        {t('office-manager.ombajadeviceactions.retiro_quipteams')}
      </Button>

      <Dialog open={pickupOpen} onOpenChange={(open) => !pickupMutation.isPending && setPickupOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('office-manager.ombajadeviceactions.retiro_con_quipteams')}</DialogTitle>
            <DialogDescription>
              {t('office-manager.ombajadeviceactions.quipteams_coordina_el_retiro', { modelo: device.modelo, serie: device.nroSerie, nombre: baja.nombre || baja.correo })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="om-personal-email">{t('office-manager.ombajadeviceactions.mail_personal')}</Label>
              <Input id="om-personal-email" type="email" value={form.personalEmail} onChange={set('personalEmail')} />
              {baja.personalEmail && (
                <p className="mt-1 text-xs text-muted-foreground">{t('office-manager.ombajadeviceactions.prellenado_desde_la_planilla_de_altas')}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="om-country">{t('office-manager.ombajadeviceactions.pais')}</Label>
                <Input id="om-country" value={form.country} onChange={set('country')} />
              </div>
              <div>
                <Label htmlFor="om-phone">{t('office-manager.ombajadeviceactions.telefono')}</Label>
                <Input id="om-phone" value={form.phone} onChange={set('phone')} />
              </div>
            </div>
            <div>
              <Label htmlFor="om-address">{t('office-manager.ombajadeviceactions.direccion_de_retiro')}</Label>
              <Input id="om-address" value={form.address} onChange={set('address')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="om-address-extra">{t('office-manager.ombajadeviceactions.piso_depto')}</Label>
                <Input id="om-address-extra" value={form.addressExtra} onChange={set('addressExtra')} />
              </div>
              <div>
                <Label htmlFor="om-when">{t('office-manager.ombajadeviceactions.cuando_contactar')}</Label>
                <Input id="om-when" value={form.whenToContact} onChange={set('whenToContact')} />
              </div>
            </div>
            <div>
              <Label htmlFor="om-notes">Notas</Label>
              <Input id="om-notes" value={form.notes} onChange={set('notes')} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" disabled={pickupMutation.isPending} onClick={() => setPickupOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={pickupMutation.isPending || !form.personalEmail || !form.country}
              onClick={() =>
                pickupMutation.mutate({
                  deviceId: device.deviceId,
                  bajaId: baja.idBaja,
                  correoWinclap: baja.correo,
                  personalEmail: form.personalEmail,
                  employeeName: baja.nombre || baja.correo,
                  country: form.country,
                  address: form.address || undefined,
                  addressExtra: form.addressExtra || undefined,
                  phone: form.phone || undefined,
                  whenToContact: form.whenToContact || undefined,
                  notes: form.notes || undefined,
                })
              }
            >
              {pickupMutation.isPending ? <WinclapLoader size={16} className="mr-2" /> : <Truck className="mr-2 h-4 w-4" />}
              {t('office-manager.ombajadeviceactions.pedir_retiro')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
